import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { QuickReply } from '../types';
import { generateId } from '../utils/generateId';

interface QuickReplyModalProps {
  onClose: () => void;
  onSave: (reply: QuickReply) => void;
}

const QuickReplyModal: React.FC<QuickReplyModalProps> = ({ onClose, onSave }) => {
  const [text, setText] = useState('');
  const [response, setResponse] = useState('');
  const [color, setColor] = useState('blue');

  const colorOptions = [
    { value: 'blue', label: 'Mavi', preview: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    { value: 'red', label: 'Kırmızı', preview: 'bg-gradient-to-r from-red-500 to-red-600' },
    { value: 'purple', label: 'Mor', preview: 'bg-gradient-to-r from-purple-500 to-purple-600' },
    { value: 'green', label: 'Yeşil', preview: 'bg-gradient-to-r from-green-500 to-green-600' },
    { value: 'yellow', label: 'Sarı', preview: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { value: 'pink', label: 'Pembe', preview: 'bg-gradient-to-r from-pink-500 to-pink-600' },
    { value: 'indigo', label: 'İndigo', preview: 'bg-gradient-to-r from-indigo-500 to-indigo-600' },
    { value: 'teal', label: 'Teal', preview: 'bg-gradient-to-r from-teal-500 to-teal-600' },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim() && response.trim()) {
      const newReply: QuickReply = {
        id: generateId(),
        text: text.trim(),
        color,
        category: 'general',
        response: response.trim(),
        variant: 'primary'
      };
      onSave(newReply);
      setText('');
      setResponse('');
      setColor('blue');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Yeni Soru ve Yanıt Ekle</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="text" className="block text-sm font-semibold text-gray-700 mb-3">
                Soru Başlığı
              </label>
              <input
                type="text"
                id="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Örnek: Şikayet başvurusu nasıl yapılır?"
                required
                autoFocus
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="response" className="block text-sm font-semibold text-gray-700 mb-3">
                Yanıt Metni
              </label>
              <textarea
                id="response"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                rows={4}
                placeholder="Bu soru için verilecek detaylı yanıt..."
                required
              />
            </div>
            
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Renk Seçimi
              </label>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((colorOption) => (
                  <button
                    key={colorOption.value}
                    type="button"
                    onClick={() => setColor(colorOption.value)}
                    className={`p-2 rounded-lg border-2 transition-all ${
                      color === colorOption.value 
                        ? 'border-gray-800 scale-105' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div className={`${colorOption.preview} h-8 w-full rounded text-white text-xs flex items-center justify-center font-medium`}>
                      {colorOption.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={!text.trim() || !response.trim()}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                <Save className="h-5 w-5 mr-2" />
                Kaydet
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickReplyModal;